import { useId, useRef, useState, type FormEvent } from "react";
import { useNativeDialog } from "../../components/useNativeDialog";
import { ApiError } from "../../lib/api-client";
import { updateFamilyNickname } from "../../lib/api-family";
import type { FamilyMember } from "../../types/family";

const MAX_NICKNAME_LEN = 255;

function mutationError(error: unknown): string {
  if (error instanceof ApiError && error.code === "FORBIDDEN") {
    return "Можно изменить только своё имя в семье.";
  }
  if (error instanceof ApiError && error.code === "VALIDATION_FAILED") {
    return "Проверьте имя: от 1 до 255 символов.";
  }
  return "Не удалось сохранить имя. Попробуйте ещё раз.";
}

export function EditFamilyNameModal({
  familyId,
  member,
  onClose,
  onUpdated,
}: {
  familyId: string;
  member: FamilyMember;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const titleId = useId();
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [nickname, setNickname] = useState(member.name);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modal = useNativeDialog(pending, onClose, inputRef);
  const trimmedNickname = nickname.trim();
  const canSubmit = trimmedNickname.length > 0 && trimmedNickname.length <= MAX_NICKNAME_LEN && !pending;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    setPending(true);
    setError(null);
    try {
      await updateFamilyNickname(familyId, trimmedNickname);
      onUpdated();
    } catch (mutationFailure) {
      setError(mutationError(mutationFailure));
      setPending(false);
    }
  }

  return (
    <dialog
      ref={modal.ref}
      onCancel={modal.onCancel}
      onKeyDown={modal.onKeyDown}
      aria-labelledby={titleId}
      aria-describedby={error ? errorId : undefined}
      className="m-auto w-[calc(100%_-_2rem)] max-w-sm rounded-[28px] border-0 bg-white p-6 text-content shadow-2xl backdrop:bg-black/40"
    >
      <h2 id={titleId} className="text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">Изменить имя</h2>
      <p className="mt-2 text-sm leading-6 text-muted">Это имя увидят участники вашей семьи.</p>
      {error && <div id={errorId} role="alert" className="mt-4 rounded-2xl bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">{error}</div>}
      <form onSubmit={submit} noValidate className="mt-5 space-y-5">
        <label htmlFor={inputId} className="block text-[13px] font-bold text-content">
          Ваше имя в семье
          <input
            ref={inputRef}
            id={inputId}
            value={nickname}
            onChange={(event) => { setNickname(event.target.value); setError(null); }}
            maxLength={MAX_NICKNAME_LEN}
            disabled={pending}
            aria-invalid={error !== null}
            aria-describedby={error ? errorId : undefined}
            className="form-control mt-2 h-12 w-full rounded-2xl border border-line bg-white px-4 focus:outline-none disabled:cursor-not-allowed"
          />
        </label>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={pending} className="h-12 cursor-pointer rounded-xl border border-line px-6 text-sm font-bold text-content disabled:cursor-not-allowed">
            Отмена
          </button>
          <button type="submit" disabled={!canSubmit} className="h-12 cursor-pointer rounded-xl bg-action px-7 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-surface-disabled">
            {pending ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
